import { useEffect, useState } from "react";
import {
  deleteProductCategory,
  readProductCategoryDeleteConflict,
} from "../../../services";
import { ProductCategory } from "../../../types/product.type";
import { numberWithCommas } from "../../../utils";
import ConfirmModal from "../../ConfirmModal";
import { STRIPE_BACKGROUND } from "../shared/constants";
import ProductCategoryDialog from "./ProductCategoryDialog";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const COLUMN_COUNT = 3;

const DELETE_FAILED_MESSAGE = "ลบหมวดสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/** Why a category cannot be deleted yet; the tooltip and the API's refusal read the same. */
const buildCategoryInUseMessage = (productCount: number): string =>
  `มีสินค้าใช้หมวดนี้ ${numberWithCommas(productCount)} รายการ`;

interface CategoryRowProps {
  category: ProductCategory;
  striped: boolean;
  onEdit: (category: ProductCategory) => void;
  onDelete: (category: ProductCategory) => void;
}

function CategoryRow({
  category,
  striped,
  onEdit,
  onDelete,
}: CategoryRowProps) {
  // A category products still use would only be refused, so the button says why instead of
  // offering the delete.
  const isInUse = category.product_count > 0;
  const deleteHint = isInUse
    ? buildCategoryInUseMessage(category.product_count)
    : "ลบ";

  const handleEdit = () => onEdit(category);
  const handleDelete = () => onDelete(category);

  return (
    <TableRow sx={{ bgcolor: striped ? STRIPE_BACKGROUND : "#fff" }}>
      <TableCell>{category.code}</TableCell>
      <TableCell>{category.name}</TableCell>
      <TableCell>
        <Stack direction="row" gap={0.5} justifyContent="center">
          <Tooltip title="แก้ไขชื่อ">
            <IconButton size="small" color="primary" onClick={handleEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={deleteHint}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isInUse}
                onClick={handleDelete}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

interface ProductCategoryListDialogProps {
  open: boolean;
  /** The screen's category list, so the filter and the table pick up every change made here. */
  categories: ProductCategory[];
  onClose: () => void;
  /** Re-reads the screen's category list after a category was added, renamed or refused. */
  onChanged: () => Promise<void>;
}

/** Head office's หมวดสินค้า lookup: add, rename and delete in one place. */
export default function ProductCategoryListDialog({
  open,
  categories,
  onClose,
  onChanged,
}: ProductCategoryListDialogProps) {
  const [formOpen, setFormOpen] = useState<boolean>(false);
  // The category the form renames; null while it adds a new one.
  const [editingCategory, setEditingCategory] =
    useState<ProductCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductCategory | null>(
    null,
  );
  const [deleting, setDeleting] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  // A refusal from an earlier visit says nothing about the list the reader reopens.
  useEffect(() => {
    if (open) setAlertMessage("");
  }, [open]);

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormOpen(true);
  };

  const handleEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleCategorySaved = () => {
    onChanged();
  };

  const handleAskDelete = (category: ProductCategory) => {
    setAlertMessage("");
    setDeleteTarget(category);
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProductCategory(deleteTarget.uuid);
      await onChanged();
    } catch (error) {
      // The request suppresses the error toast, so every failure is reported here. A 409
      // means products took the category up since the list was read, so the list is read
      // again to show the row as in use.
      const conflict = readProductCategoryDeleteConflict(error);
      if (conflict) {
        setAlertMessage(buildCategoryInUseMessage(conflict.product_count));
        await onChanged();
      } else {
        setAlertMessage(DELETE_FAILED_MESSAGE);
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDismissAlert = () => setAlertMessage("");

  const deleteDescription = deleteTarget
    ? `ต้องการลบหมวดสินค้า ${deleteTarget.code} ${deleteTarget.name} ใช่หรือไม่`
    : "";

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>หมวดหมู่สินค้า</DialogTitle>
        <DialogContent>
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button startIcon={<AddIcon />} onClick={handleOpenCreate}>
              เพิ่มหมวดสินค้า
            </Button>
          </Stack>
          {alertMessage && (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              onClose={handleDismissAlert}
            >
              {alertMessage}
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead
                sx={{ bgcolor: (theme) => theme.palette.background.default }}
              >
                <TableRow>
                  <TableCell>รหัส</TableCell>
                  <TableCell>ชื่อหมวดสินค้า</TableCell>
                  <TableCell align="center">การดำเนินการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <Typography color="text.secondary" fontWeight={500}>
                        ไม่พบข้อมูลหมวดสินค้า
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {categories.map((category, index) => (
                  <CategoryRow
                    key={category.uuid}
                    category={category}
                    striped={index % 2 === 1}
                    onEdit={handleEdit}
                    onDelete={handleAskDelete}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <ProductCategoryDialog
        open={formOpen}
        category={editingCategory}
        onClose={handleCloseForm}
        onSaved={handleCategorySaved}
      />

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="ลบหมวดสินค้า"
        description={deleteDescription}
        confirmLabel="ลบ"
        confirmColor="error"
        loading={deleting}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
